import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bot, Plus, X, Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Customer Service',
  'Data Analysis',
  'Content Creation',
  'Software Development',
  'Marketing',
  'Research',
  'Translation',
  'Legal',
  'Finance',
  'Healthcare'
];

export default function PostAgent() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [demoUrl, setDemoUrl] = useState('');
  const [capabilities, setCapabilities] = useState([]);
  const [newCapability, setNewCapability] = useState('');

  const addCapability = () => {
    if (newCapability.trim() && !capabilities.includes(newCapability.trim())) {
      setCapabilities([...capabilities, newCapability.trim()]);
      setNewCapability('');
    }
  };

  const removeCapability = (cap) => {
    setCapabilities(capabilities.filter(c => c !== cap));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !description || !category || !hourlyRate) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (capabilities.length === 0) {
      toast.error('Please add at least one capability');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/agents`, {
        name,
        description,
        category,
        hourly_rate: parseFloat(hourlyRate),
        avatar_url: avatarUrl || null,
        demo_url: demoUrl || null,
        capabilities
      }, {
        headers: getAuthHeaders()
      });

      toast.success('Agent listed successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.user_type !== 'agent_owner') {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-4">Access Denied</h2>
          <p className="text-zinc-400 mb-6">Only agent owners can list agents</p>
          <Button onClick={() => navigate('/auth?mode=register&type=agent_owner')}>
            Sign up as Agent Owner
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="post-agent-page">
      <div className="max-w-2xl mx-auto px-6">
        {/* Back button */}
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          aria-label="Go back to dashboard"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back to Dashboard
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">List Your Agent</h1>
              <p className="text-zinc-500 text-sm">Add your AI agent to the marketplace</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" aria-label="Create new agent form">
            <div>
              <Label htmlFor="agent-name" className="text-zinc-300 mb-2 block">Agent Name *</Label>
              <Input
                id="agent-name"
                placeholder="e.g., DataBot Pro"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-zinc-800 border-white/10"
                data-testid="agent-name"
                aria-required="true"
              />
            </div>

            <div>
              <Label htmlFor="agent-description" className="text-zinc-300 mb-2 block">Description *</Label>
              <Textarea
                id="agent-description"
                placeholder="Describe what your agent does, its strengths, and use cases..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-zinc-800 border-white/10 min-h-[120px]"
                data-testid="agent-description"
                aria-required="true"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300 mb-2 block">Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="bg-zinc-800 border-white/10" data-testid="agent-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-zinc-300 mb-2 block">Hourly Rate (USD) *</Label>
                <Input
                  type="number"
                  placeholder="50"
                  min="1"
                  step="0.01"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  className="bg-zinc-800 border-white/10"
                  data-testid="agent-rate"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-300 mb-2 block">Capabilities *</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Add a capability..."
                  value={newCapability}
                  onChange={(e) => setNewCapability(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCapability())}
                  className="bg-zinc-800 border-white/10"
                  data-testid="capability-input"
                />
                <Button 
                  type="button" 
                  onClick={addCapability}
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {capabilities.map((cap) => (
                  <span 
                    key={cap}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-lg text-cyan-400 text-sm"
                  >
                    {cap}
                    <button 
                      type="button"
                      onClick={() => removeCapability(cap)}
                      className="hover:text-red-400"
                      aria-label={`Remove ${cap} capability`}
                    >
                      <X className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-zinc-300 mb-2 block">Avatar URL (optional)</Label>
              <Input
                placeholder="https://example.com/avatar.png"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                className="bg-zinc-800 border-white/10"
                data-testid="agent-avatar"
              />
            </div>

            <div>
              <Label className="text-zinc-300 mb-2 block">Demo URL (optional)</Label>
              <Input
                placeholder="https://demo.myagent.com"
                value={demoUrl}
                onChange={(e) => setDemoUrl(e.target.value)}
                className="bg-zinc-800 border-white/10"
                data-testid="agent-demo"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 rounded-none mt-6"
              data-testid="submit-agent"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Bot className="w-4 h-4 mr-2" />
                  List Agent
                </>
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
